import { AskCitation } from '../../features/document-ocr/document-ocr.models';

export interface ChatMessageLine {
  text: string;
  citations: AskCitation[];
}

const CASE_MARKER_RE = /\{\{CASE:([\d,\s]+)\}\}/g;

/**
 * Splits an assistant answer into its bullet lines, pulling each line's own {{CASE:id,id}}
 * marker(s) — emitted by rag-service.js, one per bullet — into that line's citations. This is
 * what puts the case card directly under the point it supports instead of dumping every cited
 * case in one block at the end of the message, disconnected from which point it backs up.
 */
export function parseMessageLines(text: string, citations: AskCitation[] = []): ChatMessageLine[] {
  const byId = new Map(citations.map((c) => [Number(c.grievanceId), c]));
  // A case cited across several consecutive bullets (the model repeating the same
  // {{CASE:id}} marker on each) only gets its card shown the first time — repeating an
  // identical link under every one of those bullets is just noise, not new information.
  const alreadyShown = new Set<number>();
  return String(text || '')
    .split('\n')
    .map((line) => {
      const ids: number[] = [];
      const cleaned = line
        .replace(CASE_MARKER_RE, (_match, idsStr: string) => {
          for (const part of idsStr.split(',')) {
            const id = Number(part.trim());
            if (Number.isFinite(id)) ids.push(id);
          }
          return '';
        })
        .trimEnd();
      const lineCitations = ids
        .filter((id) => !alreadyShown.has(id))
        .map((id) => {
          alreadyShown.add(id);
          return byId.get(id);
        })
        .filter((c): c is AskCitation => c != null);
      return { text: cleaned, citations: lineCitations };
    })
    .filter((line) => line.text.length > 0 || line.citations.length > 0);
}
